import { CONTRACT_URL, PAYMENT_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { IApiResponse } from "../../models";
import { buildQueryString } from "../../../util/buildQueryString";
import {
  IMultiCashoutResponseData,
  IMultiCashoutFilterParams,
  IMultiCashoutPaymentsParams,
  IMultiCashoutDetailsResponseData,
  ICreateMultiCashoutRequest,
  ICreateMultiCashoutResponseData,
  IMultiCashoutCountByTypeResponseData,
  WithdrawType,
  IMultiCashoutPaymentResponseData,
} from "./models";
const defaultProvide = async () => await getToken();

export const getWaitingMultiCashouts = async ({
  userId,
  domainId,
  page,
  size,
}: {
  userId?: string;
  domainId?: string;
  page: number;
  size: number;
}): Promise<IApiResponse<IMultiCashoutResponseData>> => {
  const queryString = buildQueryString({ userId, domainId, page, size });
  const url = `${CONTRACT_URL}grouping-non-cashed-out-by-user${
    queryString ? `?${queryString}` : ""
  }`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  const response = await fetchRequest(descriptor);
  return response as IApiResponse<IMultiCashoutResponseData>;
};

export const getMultiCashoutPayments = async ({
  page = 0,
  size = 10,
  domainId,
  beneficiaryId,
  statuses,
}: IMultiCashoutPaymentsParams): Promise<
  IApiResponse<IMultiCashoutPaymentResponseData>
> => {
  const queryString = buildQueryString({
    statuses: statuses,
    withdrawType: WithdrawType.MULTI_CASH_OUT,
    page,
    size,
    domainId,
    beneficiaryId,
  });
  const url = `${PAYMENT_BASE_URL}withdrawal${
    queryString ? `?${queryString}` : ""
  }`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  const response = await fetchRequest(descriptor);
  return response as IApiResponse<IMultiCashoutPaymentResponseData>;
};

export const getCurrentMultiCashouts = async ({
  page = 0,
  size = 10,
  domainId,
  beneficiaryId,
}: IMultiCashoutFilterParams): Promise<
  IApiResponse<IMultiCashoutPaymentResponseData>
> => {
  return getMultiCashoutPayments({
    page,
    size,
    domainId,
    beneficiaryId,
    statuses: "INITIATED,IN_PROGRESS,PENDING",
  });
};

export const getFailedMultiCashouts = async ({
  page = 0,
  size = 10,
  domainId,
  beneficiaryId,
}: IMultiCashoutFilterParams): Promise<
  IApiResponse<IMultiCashoutPaymentResponseData>
> => {
  return getMultiCashoutPayments({
    page,
    size,
    domainId,
    beneficiaryId,
    statuses: "FAILED",
  });
};

export const getMultiCashoutDetails = async ({
  cashOutParticipantId,
  sellerPage_page,
  sellerPage_size,
  buyerPage_page,
  buyerPage_size,
  userRoles = "PROVIDER, CUSTOMER",
}: {
  cashOutParticipantId?: string;
  sellerPage_page?: number;
  sellerPage_size?: number;
  buyerPage_page?: number;
  buyerPage_size?: number;
  userRoles?: string;
}): Promise<IApiResponse<IMultiCashoutDetailsResponseData>> => {
  const queryString = buildQueryString({
    cashOutParticipantId,
    sellerPage_page,
    sellerPage_size,
    buyerPage_page,
    buyerPage_size,
    userRoles,
  });
  const url = `${CONTRACT_URL}multi-cashOut/checkout${
    queryString ? `?${queryString}` : ""
  }`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  const response = await fetchRequest(descriptor);
  return response as IApiResponse<IMultiCashoutDetailsResponseData>;
};

export const createMultiCashout = async ({
  beneficiaryId,
  amount,
  beneficiaryName,
  withdrawalExecute,
  withdrawType = WithdrawType.MULTI_CASH_OUT,
}: ICreateMultiCashoutRequest): Promise<
  IApiResponse<ICreateMultiCashoutResponseData>
> => {
  const url = `${PAYMENT_BASE_URL}withdrawal`;
  const descriptor = new ApiServiceManager(url, "POST", defaultProvide);
  const body = {
    beneficiaryId,
    amount,
    beneficiaryName,
    withdrawType,
    withdrawalExecute,
  };
  const response = await fetchRequest(descriptor, body);
  return response as IApiResponse<ICreateMultiCashoutResponseData>;
};

export const getMultiCashoutCountByType = async ({
  domainId,
  beneficiaryId,
}: {
  domainId?: string;
  beneficiaryId?: string;
} = {}): Promise<IApiResponse<IMultiCashoutCountByTypeResponseData>> => {
  const queryString = buildQueryString({
    withdrawType: WithdrawType.MULTI_CASH_OUT,
    domainId,
    beneficiaryId,
  });
  const url = `${PAYMENT_BASE_URL}withdrawal/count-by-type${
    queryString ? `?${queryString}` : ""
  }`;
  const descriptor = new ApiServiceManager(url, "GET", defaultProvide);
  const response = await fetchRequest(descriptor);
  return response as IApiResponse<IMultiCashoutCountByTypeResponseData>;
};
